import React from 'react';

/**
 * Global error boundary — catches React render errors and shows a friendly
 * "Something went wrong, your data is safe" screen instead of a blank page.
 * Also fires a one-shot report to `/api/error-reports` so an admin email
 * goes out with the stack trace + URL + user agent.
 *
 * Class component is required — hooks can't catch render errors.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reportSent: false, isTranslatorConflict: false };
  }

  static getDerivedStateFromError(error) {
    // Browser translators (Google Translate, Chrome auto-translate,
    // Grammarly, DarkReader) mutate the DOM under React's feet. When
    // React tries to unmount a translated node, it can't find it and
    // throws NotFoundError: "Failed to execute 'removeChild' on
    // 'Node'". Flag these separately so we can show a tailored hint
    // and skip the admin-email spam (we can't fix a Chrome extension).
    const msg = String(error?.message || '');
    const isTranslator = (
      msg.includes("removeChild") ||
      msg.includes("insertBefore") ||
      msg.includes("The node to be removed") ||
      msg.includes("The node before which") ||
      msg.includes("Failed to execute 'appendChild'")
    );
    return { hasError: true, isTranslatorConflict: isTranslator };
  }

  componentDidCatch(error, info) {
    // Fire-and-forget report to backend. Wrapped in try/catch so a network
    // failure during the report can't itself crash the error screen.
    if (this.state.reportSent) return;
    // Skip admin email for translator/extension DOM-mutation errors —
    // they're not our bugs and can't be fixed from our code (only
    // mitigated with `translate="no"` on specific pages).
    if (this.state.isTranslatorConflict) {
      this.setState({ reportSent: true });
      // eslint-disable-next-line no-console
      console.warn('[ErrorBoundary] Suppressing translator-conflict report:', error?.message);
      return;
    }
    try {
      const payload = {
        message: String(error?.message || error || 'Unknown error').slice(0, 1000),
        stack: String(error?.stack || '').slice(0, 4000),
        component_stack: String(info?.componentStack || '').slice(0, 4000),
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      };
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${apiBase}/api/error-reports`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
      this.setState({ reportSent: true });
    } catch {
      // Swallow — never let reporting crash the boundary.
    }
    // Also surface in the browser console for local debugging.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      // no-op
    }
  };

  handleHome = () => {
    try {
      window.location.href = '/';
    } catch {
      // no-op
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-screen"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#df5681]/10 flex items-center justify-center mx-auto mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8 text-[#df5681]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1
            className="text-2xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Something went wrong
          </h1>
          {this.state.isTranslatorConflict ? (
            <>
              <p className="text-sm text-slate-500 mb-1">
                Looks like a browser extension (Google Translate, Grammarly,
                DarkReader, etc.) is interfering with this page.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Try one of these: turn off &quot;Translate this page&quot; in
                Chrome, disable the extension for meetformahjong.com, or open
                the page in an incognito window.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-1">
                Your data is safe — nothing was lost.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Our team has been notified. Please try reloading the page.
              </p>
            </>
          )}
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] font-semibold px-6 py-2.5 text-sm transition-colors"
              data-testid="error-boundary-reload-btn"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="rounded-full border border-slate-200 hover:border-slate-300 text-slate-700 px-6 py-2.5 text-sm transition-colors"
              data-testid="error-boundary-home-btn"
            >
              Go home
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-6">
            If this keeps happening, email{' '}
            <a
              href="mailto:meetformahjong@gmail.com"
              className="text-[#56c6df] hover:underline"
            >
              meetformahjong@gmail.com
            </a>
            .
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
